import { ChangeDetectionStrategy, Component, effect, inject, Pipe, PipeTransform, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LandingPageApiService } from './landing-page-api';
import { pageableFromParams } from '../../content.utils';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatIcon } from '@angular/material/icon';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { TruncatePipe } from '../../pipes/truncate';
import { DatePipe } from '@angular/common';
import { MatButton, MatIconAnchor } from '@angular/material/button';
import { MatFormField, MatLabel, MatPrefix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { debounceTime, mergeMap } from 'rxjs';
import { WordCountPipe } from '../../pipes/word-count';
import { PreviewUrlPipe } from '../../pipes/preview-url';

@Pipe({
  name: 'encodeQueryPathForRoute',
})
class EncodeQueryPathForRoutePipe implements PipeTransform {
  transform(value: string): string {
    return QueryPathRouteEncoder.encode(value);
  }
}

@Component({
  imports: [
    RouterLink,
    MatPaginator,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    TruncatePipe,
    DatePipe,
    MatIconAnchor,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatPrefix,
    WordCountPipe,
    EncodeQueryPathForRoutePipe,
    PreviewUrlPipe,
  ],
  template: `
    <div class="mt-8 flex justify-between">
      <div class="flex items-center gap-4">
        <mat-form-field appearance="outline" class="w-62">
          <mat-label>Search landing pages</mat-label>
          <mat-icon class="w-10!" matPrefix>search</mat-icon>
          <input
            matInput
            [value]="searchTermInput()"
            (input)="searchTermInput.set($any($event.target).value)"
            placeholder="Type to search..."
          />
        </mat-form-field>
      </div>
      <div>
        <button mat-stroked-button class="button-primary" color="primary" [routerLink]="['/content/landing-pages/new']">
          <mat-icon class="text-white!">add</mat-icon>
          Create
        </button>
      </div>
    </div>

    @let _pages = pages();
    <table mat-table [dataSource]="_pages?.content" class="w-full">
      <ng-container matColumnDef="queryPath">
        <th mat-header-cell *matHeaderCellDef>URL Path</th>
        <td mat-cell *matCellDef="let contentLandingPage">
          <a routerLink="./{{ contentLandingPage.queryPath | encodeQueryPathForRoute }}" class="cursor-pointer">
            {{ contentLandingPage.queryPath | truncate }}
          </a>
        </td>
      </ng-container>

      <ng-container matColumnDef="lastModifiedDate">
        <th mat-header-cell *matHeaderCellDef>Last modified</th>
        <td mat-cell *matCellDef="let contentLandingPage">
          {{ contentLandingPage.lastModifiedDate | date: 'dd.MM.yyyy HH:mm' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="published">
        <th mat-header-cell *matHeaderCellDef>Published</th>
        <td mat-cell *matCellDef="let contentLandingPage">
          <mat-icon class="{{ contentLandingPage.published ? 'text-primary!' : 'text-orange-500!' }}">
            {{ contentLandingPage.published ? 'check_circle' : 'pending_actions' }}
          </mat-icon>
        </td>
      </ng-container>

      <ng-container matColumnDef="word-count">
        <th mat-header-cell *matHeaderCellDef>Word count</th>
        <td mat-cell *matCellDef="let contentLandingPage">
          {{ contentLandingPage.html | wordCount }}
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let contentLandingPage">
          <a
            mat-icon-button
            target="_blank"
            [href]="contentLandingPage.queryPath | previewUrl:'s'"
            aria-label="Live preview"
          >
            <mat-icon>open_in_new</mat-icon>
          </a>
          <a
            mat-icon-button
            routerLink="./{{ contentLandingPage.queryPath | encodeQueryPathForRoute }}"
            aria-label="Edit"
          >
            <mat-icon>edit</mat-icon>
          </a>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
    </table>

    @if (_pages) {
      <div class="flex justify-center">
        <mat-paginator
          [pageSize]="_pages.size"
          [pageIndex]="_pages.number"
          [length]="_pages.totalElements"
          (page)="onPageChange($event)"
        />
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPagesComponent {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly landingPageApiService = inject(LandingPageApiService);
  private readonly router = inject(Router);

  protected readonly displayedColumns = ['queryPath', 'lastModifiedDate', 'word-count', 'published', 'actions'];
  protected readonly searchTermInput = signal('');
  protected readonly pages = toSignal(
    this.activatedRoute.queryParams.pipe(
      debounceTime(200),
      mergeMap((params) => this.landingPageApiService.getLandingPages(pageableFromParams(params), params['search'])),
    ),
  );

  constructor() {
    effect(() => this.syncSearchInputWithRouteParam(this.searchTermInput()));
  }

  protected onPageChange($event: PageEvent) {
    void this.router.navigate([], {
      queryParams: { page: $event.pageIndex, size: $event.pageSize },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Updates the current route's query parameters with a new search string.
   *
   * - Preserves existing query parameters.
   * - Resets the `page` parameter to `0` when a new search is applied.
   * - Merges parameters into the current URL without adding a new history entry.
   *
   * @param searchInput The new search query string to apply.
   */
  private syncSearchInputWithRouteParam(searchInput: string) {
    void this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        search: searchInput,
        page: 0,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

/**
 * Utility class for safely encoding and decoding query paths
 * when passing them through Angular routes and backend APIs.
 *
 * This encoder ensures that path-like values (containing `/`)
 * can be preserved as a single URL path segment without breaking
 * routing or being misinterpreted by the server.
 *
 * Strategy:
 * - Encode for routes: replace `/` with a safe replacement (`~`).
 * - Decode for routes: reverse the replacement so the backend
 *   receives the original query path with `/` restored.
 *
 * ### Example:
 *   const slug = "vw/vw-volkswagen-cc";
 *   const encoded = QueryPathEncoder.encode(pathSegment);
 *   // "vw@@vw-volkswagen-cc"
 *
 *   const decoded = QueryPathEncoder.decode(encodedPathSegment);
 *   // "vw/vw-volkswagen-cc"
 */
export class QueryPathRouteEncoder {
  private static SAFE_REPLACEMENT = '@@';

  static encode(queryPath: string): string {
    queryPath = queryPath.replace(/\//g, QueryPathRouteEncoder.SAFE_REPLACEMENT);
    return queryPath;
  }

  static decode(queryPath: string): string {
    queryPath = queryPath.replace(new RegExp(QueryPathRouteEncoder.SAFE_REPLACEMENT, 'g'), '/');
    return queryPath;
  }
}
